import React, { memo, useCallback, useMemo } from 'react'
import { Theme } from '@material-ui/core'
import { useTheme, makeStyles } from '@material-ui/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import IconButton from '@material-ui/core/IconButton'
import CloseIcon from '@material-ui/icons/CloseRounded'
import classNames from 'classnames'
import Typography from '@material-ui/core/Typography'
import Hidden from '@material-ui/core/Hidden'
import { useTranslation } from 'react-i18next'
import KeyboardArrowDown from '@material-ui/icons/KeyboardArrowDownOutlined'
import SwipeableDrawer from '@material-ui/core/SwipeableDrawer'
import Popper from '@material-ui/core/Popper'
import ClickAwayListener from '@material-ui/core/ClickAwayListener'
import Paper from '@material-ui/core/Paper'
import * as _ from 'lodash'

import { MenuContent } from './components'
import { Badge } from 'components'
import styles from './styles'
import { useArticleState } from '../../reducer'
import {
	EArticleSectionTypes,
	EArticleActionTypes,
	TArticleHeaderProps,
	EEntity,
	ICompany,
} from 'models'
import { StoreValue } from 'store'
import { EnumUserRoles } from 'utils'

const useStyles = makeStyles(styles)

const HeaderView: React.FunctionComponent<TArticleHeaderProps> = ({
	storeUser,
	closeArticle,
}) => {
	const menuRef: React.Ref<any> = React.useRef(null)
	const arrowRef: React.Ref<any> = React.useRef(null)
	const theme: Theme = useTheme()
	const isSmDown: boolean = useMediaQuery(theme.breakpoints.down('sm'))
	const classes = useStyles()
	const { t } = useTranslation()
	const [state, dispatch] = useArticleState()
	const [isMenuVisible, setIsMenuVisible] = React.useState(false)
	const { section } = state
	const author = state.author || storeUser
	const postAs = state.postAs
	const postAsId = state.postAsId
	const showMenu = useCallback(() => {
		setIsMenuVisible(true)
	}, [])
	const closeMenu = useCallback(
		(event: React.MouseEvent | React.ChangeEvent<any>) => {
			if (event && menuRef && menuRef.current) {
				if (menuRef.current.contains(event.target)) return null
			}
			setIsMenuVisible(false)
		},
		[],
	)
	const toggleSection = useCallback(
		(section: EArticleSectionTypes) => () => {
			dispatch({
				type: EArticleActionTypes.setSection,
				section,
			})
		},
		[dispatch],
	)

	// select all the companies where the user is at least a publisher
	const userCompanies = useMemo(() => {
		const companyRoles = storeUser && storeUser.companyRoles
		const companiesWithPublisherRole = companyRoles
			.filter((item: any) => {
				return item.roles.includes(EnumUserRoles.PUBLISHER)
			})
			.map((item: any) => {
				return item.company
			})
		let mergedData = companyRoles
		if (storeUser?.profile?.ownedCompanies) {
			mergedData = companiesWithPublisherRole.concat(
				storeUser?.profile?.ownedCompanies,
			)
		}
		const freeDuplicates = _.uniqBy(mergedData, 'id')
		return freeDuplicates as ICompany[]
	}, [storeUser])

	const handlePostAs = useCallback(
		(type: EEntity, company?: ICompany) => {
			dispatch({
				type: EArticleActionTypes.setPostAs,
				postAs: type,
				postAsId:
					type === EEntity.person
						? author && author.id
						: company && company.id,
			})
		},
		[dispatch, author],
	)

	const selectedCompany: ICompany | undefined = useMemo(() => {
		return (
			userCompanies &&
			userCompanies.find((item: ICompany) => item.id === postAsId)
		)
	}, [userCompanies, postAsId])

	return (
		<div className={classes.container}>
			<div className={classes.leftMenu}>
				<IconButton
					className={classes.closeButton}
					onClick={closeArticle}
				>
					<CloseIcon />
				</IconButton>
			</div>
			<div className={classes.middleMenu}>
				<Typography
					onClick={toggleSection(EArticleSectionTypes.post)}
					variant="h6"
					color="textPrimary"
					className={classNames(classes.menuItem, {
						[classes.menuActive]:
							section === EArticleSectionTypes.post,
					})}
				>
					<Hidden smDown>{t('Write a post')}</Hidden>
					<Hidden mdUp>{t('Post')}</Hidden>
				</Typography>
				<Typography
					onClick={toggleSection(EArticleSectionTypes.article)}
					variant="h6"
					color="textSecondary"
					className={classNames(classes.menuItem, {
						[classes.menuActive]:
							section === EArticleSectionTypes.article,
					})}
				>
					<Hidden mdDown>{t('Write a Long Article')}</Hidden>
					<Hidden lgUp>{t('Article')}</Hidden>
				</Typography>
			</div>
			{
				<div className={classes.rightMenu}>
					{/*<div*/}
					{/*	className={classes.avatarContainer}*/}
					{/*	onClick={showMenu}*/}
					{/*	ref={menuRef}*/}
					{/*>*/}
					{/*	<Badge*/}
					{/*		type={postAs}*/}
					{/*		name={t('Post as')}*/}
					{/*		title={t('Post as')}*/}
					{/*		extraClasses={classes.postAs}*/}
					{/*		extraAvatarClasses={classes.avatar}*/}
					{/*		extraInfoClasses={classes.info}*/}
					{/*		user={*/}
					{/*			(postAs === EEntity.person && author) ||*/}
					{/*			undefined*/}
					{/*		}*/}
					{/*		company={*/}
					{/*			(postAs === EEntity.company &&*/}
					{/*				selectedCompany) ||*/}
					{/*			undefined*/}
					{/*		}*/}
					{/*		// avatarOnly*/}
					{/*	/>*/}
					{/*	/!*{isSmDown && <AddRounded />}*!/*/}
					{/*	{!isSmDown && <KeyboardArrowDown />}*/}
					{/*</div>*/}
					<div
						className={classes.avatarContainer}
						onClick={showMenu}
						ref={menuRef}
					>
						<Badge
							type={postAs}
							name={t('Post as')}
							title={t('Post as')}
							extraClasses={classes.postAs}
							extraAvatarClasses={classes.avatar}
							extraInfoClasses={classes.info}
							user={
								(postAs === EEntity.person && author) ||
								undefined
							}
							company={
								(postAs === EEntity.company &&
									selectedCompany) ||
								undefined
							}
							avatarOnly
						/>
						{!isSmDown && (
							<div className={classes.sidebarHeader}>
								{t('Post as')}
							</div>
						)}
						<KeyboardArrowDown />
					</div>
					{!isSmDown && (
						<Popper
							className={classes.popper}
							open={isMenuVisible}
							placement="bottom-end"
							anchorEl={menuRef.current}
							disablePortal={true}
							modifiers={{
								flip: {
									enabled: true,
								},
								preventOverflow: {
									enabled: true,
								},
								arrow: {
									enabled: !!arrowRef && arrowRef.current,
									element: arrowRef && arrowRef.current,
								},
							}}
						>
							<span className={classes.arrow} ref={arrowRef} />
							<Paper elevation={2}>
								<ClickAwayListener onClickAway={closeMenu}>
									<div>
										<MenuContent
											closeMenu={closeMenu}
											userCompanies={
												userCompanies && userCompanies
											}
											onClick={handlePostAs}
											articleAuthor={author}
										/>
									</div>
								</ClickAwayListener>
							</Paper>
						</Popper>
					)}
					{isSmDown && (
						<SwipeableDrawer
							anchor="right"
							open={isMenuVisible}
							onOpen={showMenu}
							onClose={closeMenu}
							classes={{
								paper: classes.sidebarDrawer,
							}}
						>
							<MenuContent
								closeMenu={closeMenu}
								userCompanies={userCompanies && userCompanies}
								onClick={handlePostAs}
								articleAuthor={author}
							/>
						</SwipeableDrawer>
					)}
				</div>
			}
		</div>
	)
}

const Header = memo((props: TArticleHeaderProps) => (
	<StoreValue keys={['user|storeUser']}>
		<HeaderView {...props} />
	</StoreValue>
))

export default Header
